<template>
  <base-material-card
    color="primary"
    icon="book"
    inline
    class="px-5 py-3"
  >
    <template v-slot:after-heading>
      <div class="text-h2 font-weight-light">
        {{ $tc("specifications", 99) }}
      </div>
    </template>
    <v-row>
      <v-col :cols="2">
        <v-select
          v-model="importer"
          :placeholder="$tc('importer')"
          :items="importers"
          item-text="name"
        />
      </v-col>
      <v-col :cols="6">
        <v-file-input
          v-model="files"
          :label="`${$tc('add_new')} ${$tc('specifications')}`"
          accept=".csv,.xml,.xls,.xlsx"
          multiple
          :disabled="uploadDisabled"
          :loading="uploading"
          @change="uploadFiles"
        >
          <template v-slot:selection="{ text }">
            <v-chip
              small
              label
              color="primary"
            >
              {{ text }}
            </v-chip>
          </template>
        </v-file-input>
      </v-col>

      <v-col>
        <v-btn
          :disabled="!unprocessedSpecifications"
          :color="unprocessedSpecifications && 'primary'"
          @click="readSpecifications()"
        >
          {{ $tc('load') + ' (' + totalItems + ')' }}
        </v-btn>
        <v-btn
          :color="'primary'"
          @click="processSpecifications()"
        >
          {{ $tc('process') + ' (' + totalItems + ')' }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mx-0">
      <v-col cols="6">
        <filter-main
          :filter-options="filterOptions"
          :filter-actions="filterActions"
          :filter="filter"
          @set-filter="setFilter"
        />
      </v-col>
    </v-row>
    <v-data-table
      dense
      :items="items"
      :headers="specifications_headers"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      :footer-props="footerProps"
    >
      <template v-slot:body.prepend>
        <filter-row
          :filter-options="filterOptions"
          :headers="specifications_headers"
          :filter="filter"
          @set-filter="setFilter"
        />
      </template>
      <template v-slot:item.progress="{ item }">
        <v-chip :style="getProgressStyle(progress[item.id])">
          {{ progress[item.id] ? parseInt(progress[item.id]) +'%' : '' }}
        </v-chip>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip :style="getStatusStyle(item)">
          {{ $_.capitalize(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.created_at="{item}">
        {{ convertUTCToLocal(item.created_at) }}
      </template>

      <template v-slot:item.action="{ item }">
        <v-btn
          class="px-1"
          small
          color="primary"
          :disabled="!actionButtonEnabled(item)"
          @click="handleActionClick(item)"
        >
          {{ actionButtonText(item) }}
        </v-btn>
        <v-btn
          v-if="showDeleteButton(item.status)"
          class="ml-1 px-1 w-100"
          small
          color="red"
          @click="deleteSpecification(item)"
        >
          {{ $t('delete') }}
        </v-btn>
      </template>
    </v-data-table>
  </base-material-card>
</template>

<script>
  import List from '@/mixins/List'
  import _ from 'lodash/fp'
  import i18n from '@/i18n'
  export default {
    mixins: [List],
    data (vm) {
      return {
        shortPoll: null,
        endpoint: 'specifications',
        name: 'specifications',
        importers: [],
        importer: null,
        uploading: false,
        loading: false,
        files: [],
        progress: {
        },

        specifications_headers: [
          {
            sortable: false,
            text: i18n.t('progress'),
            value: 'progress',
            align: 'center',
          },
          {
            sortable: false,
            text: i18n.t('id'),
            value: 'id',
          },
          {
            sortable: true,
            text: i18n.t('carrier'),
            value: 'carrier',
            align: 'center',
          },
          {
            sortable: false,
            text: i18n.t('filename'),
            value: 'filename',
            width: '100%',
          },
          {
            sortable: false,
            text: i18n.t('specification_line'),
            value: 'lines',
            align: 'center',
          },
          {
            sortable: false,
            text: i18n.t('price'),
            value: 'total_price',
          },
          {
            sortable: true,
            text: i18n.t('status'),
            value: 'status',
          },
          {
            sortable: true,
            text: i18n.t('created'),
            value: 'created_at',
          },
          { text: i18n.t('actions'), value: 'action', sortable: false },

        ],
        filterOptions: {

          carrier: {
            label: i18n.t('carrier'),
            type: 'multi',
            options: vm.$store.state.carriers,
            filter: [],
          },
          filename: {
            label: i18n.t('filename'),
            type: 'free',
          },
          status: {
            label: i18n.t('status'),
            type: 'multi',
            options: [{ slug: 'read', label: 'Read' }, { slug: 'processed', label: 'Processed' }, { slug: 'invoiced', label: 'Invoiced' }, { slug: 'processing', label: 'Processing' }],
            filter: [],
          },
          created_at: {
            label: i18n.t('created'),
            type: 'date-range',
            filter: {},
          },
        },
      }
    },
    computed: {
      uploadDisabled: function () {
        return !this.importer || this.uploading
      },
      unprocessedSpecifications: function () {
        return this.$_.countBy(this.items, s => s.status === 'initial').true
      },
    },
    watch: {
      items: function (newVal, oldVal) {
        if (!oldVal || !oldVal.length) {
          this.setupSockets()
        }
      },
    },
    destroyed: function () {
      this.items.forEach(spec => {
        this.$echo.private(`specification.${spec.id}`)
          .stopListening('.statusUpdated')
          .stopListening('.progressUpdated')
      })
    },
    created: function () {
      this.getDataFromApi()
      this.fetchCarriers()
    },
    methods: {
      setupSockets () {
        const progress = {}
        this.items.forEach(spec => { progress[spec.id] = spec.progress })
        this.progress = progress
        this.items.forEach(spec => {
          this.$echo.private(`specification.${spec.id}`)
            .listen('.statusUpdated', (data) => {
              const index = this.$_.findIndex(this.items, ['id', spec.id])

              if (index >= 0) {
                this.items = _.set(`${index}.status`, data[0], this.items)
                this.$toast(this.$i18n.t('spec_status_updated', { spec: this.items[index].filename, status: data[0] }))
              }
            })
            .listen('.progressUpdated', (data) => {
              const progress = { ...this.progress }
              progress[spec.id] = data[0]
              this.progress = progress
            })
        })
      },
      showDeleteButton: function (status) {
        return ['initial', 'reading', 'read', 'processed'].includes(status)
      },
      handleActionClick: function (item) {
        switch (item.status) {
          case 'initial':
            this.readSpecifications(item)
            break
          case 'read':
          case 'processed':
            this.processSpecifications(item)
            break
          default:
            break
        }
      },
      actionButtonText: function (item) {
        switch (item.status) {
          case 'initial':
            return i18n.t('load')
          case 'read':
          case 'processed':
            return i18n.t('process')
          default:
            return i18n.t('locked')
        }
      },
      actionButtonEnabled: function (item) {
        switch (item.status) {
          case 'initial':
          case 'read':
          case 'processed':
            return true
          default:
            return false
        }
      },
      getStatusStyle: function (item) {
        switch (item.status) {
          case 'initial':
            return { backgroundColor: 'lightgreen' }
          case 'queued_for_reading':
          case 'reading':
            return { backgroundColor: 'orange' }
          case 'read':
            return { backgroundColor: 'lightgreen' }
          case 'queued_for_processing':
          case 'processing':
            return { backgroundColor: 'orange' }
          case 'processed':
            return { backgroundColor: 'green', color: 'white' }
          case 'invoiced':
            return { backgroundColor: 'grey', color: 'white' }
          default:
            return ''
        }
      },
      getProgressStyle: function (progress = 0) {
        const percentage = parseInt(progress) / 100
        var hue = (percentage * 120).toString(10)
        return `backgroundColor: ${['hsl(', hue, ',73%,75%)'].join('')}`
      },
      fetchCarriers: function () {
        this.loading = true
        this.$get(`/${this.endpoint}/upload/carriers`).then(response => {
          const importers = this.$_.map(response.data.response_data, (d, key) => {
            return { name: key, value: d }
          })
          this.importers = importers
          this.loading = false
        })
          .catch(e => {
            this.loading = false
          })
      },
      uploadFiles: function () {
        this.uploading = true
        const formData = new FormData()
        for (var i = 0; i < this.files.length; i++) {
          const file = this.files[i]

          formData.append(`files[${i}]`, file)
        }
        this.$post(this.importer, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).then(response => {
          this.uploading = false
          this.getDataFromApi()
          this.files = []
        })
          .catch(e => {
            this.uploading = false
          })
      },
      readSpecifications: function (spec = {}) {
        const filename = spec.filename || `${i18n.t('all').toLowerCase()} ${i18n.t('specifications').toLowerCase()}`
        const confirmText = this.$t('confirm_read_specification', { name: filename })
        if (confirm(confirmText)) {
          this.loading = true

          this.$put(`/${this.endpoint}/read${spec.id ? `/${spec.id}` : ''}`, { filters: this.filters })
            .then(r => {
              this.loading = false
            })
            .catch(e => {
              this.loading = false
            })
        }
      },
      processSpecifications: function (spec = {}) {
        const filename = spec.filename || `${i18n.t('all').toLowerCase()} ${i18n.t('specifications').toLowerCase()}`
        const confirmText = i18n.t('confirm_process_specification', { name: filename })
        if (confirm(confirmText)) {
          this.loading = true

          this.$put(`/${this.endpoint}/process${spec.id ? `/${spec.id}` : ''}`, { filters: this.filters })
            .then(r => {
              this.loading = false
            })
            .catch(e => {
              this.loading = false
            })
        }
      },
      deleteSpecification: function (spec) {
        const confirmText = i18n.t('confirm_delete', { type: spec.filename })
        if (confirm(confirmText)) {
          this.loading = true
          this.$del(`/${this.endpoint}${spec.id ? `/${spec.id}` : ''}`).then(response => {
            this.loading = false

            const specs = [...this.items]
            const index = this.$_.findIndex(specs, ['id', spec.id])
            specs.splice(index, 1)
            this.items = specs
            this.totalItems--
          })
            .catch(e => {
              this.loading = false
            })
        }
      },
    },
  }
</script>
